import Breadcrumbs from '../components/Breadcrumbs';
import { icons } from '../components/Icons';
import { AuthenticatedLayout } from '../components/Layout/AuthenticatedLayout';
import { CustomPaper } from '../components/Layout/CustomPaper';
import { UnauthenticatedLayout } from '../components/Layout/UnauthenticatedLayout';
import { authClient } from '../lib/authClient';
import { trpc } from '../lib/trpc';
import { routes } from '../routes';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { Button, Container, Space, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm } from 'react-hook-form';
import { Link, Redirect, useSearchParams } from 'wouter';
import { z } from 'zod/v4';

const schema = z.object({
  password: z.string().min(1, 'Password is required'),
  passwordConfirmation: z.string().min(1, 'Password confirmation is required'),
});

export const ResetPassword = () => {
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm<z.infer<typeof schema>>({
    resolver: standardSchemaResolver(schema),
  });

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { data: session, isPending } = authClient.useSession();

  const { mutateAsync: updatePasswordAsync } =
    trpc.profile.updatePassword.useMutation();

  const submitHandler = handleSubmit(async (data) => {
    if (data.password !== data.passwordConfirmation) {
      setError('passwordConfirmation', {
        message: 'Passwords do not match',
      });
      return;
    }

    if (token) {
      const { error } = await authClient.resetPassword({
        newPassword: data.password,
        token,
      });
      if (error) {
        notifications.show({
          message: (
            <>
              <icons.Failure /> {error.message}
            </>
          ),
        });
      } else {
        notifications.show({
          message: (
            <>
              <icons.Success /> Password successfully updated
            </>
          ),
        });
        // navigate(`~${routes.signIn}`);
      }
    } else if (session?.user) {
      await updatePasswordAsync({ password: data.password });
      reset();
      notifications.show({
        message: (
          <>
            <icons.Success /> Password successfully updated
          </>
        ),
      });
    } else {
      // navigate(`~${routes.signIn}`);
    }
  });

  if (isPending) {
    return null;
  }

  const content = (
    <form
      noValidate
      onSubmit={submitHandler}
    >
      <Space h="lg" />
      <TextInput
        error={errors?.password?.message?.toString()}
        label="Password"
        type="password"
        {...register('password', {
          required: 'This field is required',
        })}
      />
      <Space h="md" />
      <TextInput
        error={errors?.passwordConfirmation?.message?.toString()}
        label="Password confirmation"
        type="password"
        {...register('passwordConfirmation', {
          required: 'This field is required',
        })}
      />
      <Space h="xl" />
      <Button
        disabled={isSubmitting}
        loading={isSubmitting}
        type="submit"
      >
        Save
      </Button>
      <Button
        className="ms-3"
        component={Link}
        to={`~${routes.signIn}`}
        variant="outline"
      >
        Cancel
      </Button>
    </form>
  );

  if (session?.session && session?.user) {
    return (
      <AuthenticatedLayout>
        <Container size="md">
          <Breadcrumbs items={[{ '/': 'Home' }, 'Reset password']} />
          <Container
            size="xs"
            style={{ padding: 40 }}
          >
            {content}
          </Container>
        </Container>
      </AuthenticatedLayout>
    );
  }

  if (!token) {
    return <Redirect to={routes.signIn} />;
  }

  return (
    <UnauthenticatedLayout>
      <CustomPaper>
        <Title order={4}>Reset your password</Title>
        {content}
      </CustomPaper>
    </UnauthenticatedLayout>
  );
};
